import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package,
  Search,
  Filter,
  RefreshCw,
  Eye,
  CheckCircle,
  Clock,
  Truck,
  MapPin,
  Phone,
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Upload,
  FileSpreadsheet,
  XCircle,
  Building2,
  CreditCard,
  Banknote,
  ChefHat,
  PackageCheck,
  Settings,
  CheckSquare,
  Square,
  MoreHorizontal,
  X,
  AlertTriangle,
  User,
  Edit,
  History,
  Save,
  Trash2,
  Plus,
  Minus,
} from "lucide-react";

// Configuración de estados unificados
const ESTADOS_CONFIG = {
  // Estados del catálogo digital
  recibido: { icon: Clock, label: "Recibido", color: "bg-gray-500", textColor: "text-gray-600", bgLight: "bg-gray-50" },
  pendiente_confirmacion: { icon: AlertTriangle, label: "Pend. Confirmación", color: "bg-amber-500", textColor: "text-amber-600", bgLight: "bg-amber-50" },
  pago_pendiente: { icon: CreditCard, label: "Pago Pendiente", color: "bg-yellow-500", textColor: "text-yellow-600", bgLight: "bg-yellow-50" },
  pago_confirmado: { icon: CheckCircle, label: "Pago Confirmado", color: "bg-green-500", textColor: "text-green-600", bgLight: "bg-green-50" },
  ya_pagado: { icon: CheckCircle, label: "Ya Pagado", color: "bg-emerald-500", textColor: "text-emerald-600", bgLight: "bg-emerald-50" },
  confirmado: { icon: CheckCircle, label: "Confirmado", color: "bg-green-500", textColor: "text-green-600", bgLight: "bg-green-50" },
  en_produccion: { icon: ChefHat, label: "En Producción", color: "bg-orange-500", textColor: "text-orange-600", bgLight: "bg-orange-50" },
  listo: { icon: PackageCheck, label: "Listo", color: "bg-purple-500", textColor: "text-purple-600", bgLight: "bg-purple-50" },
  en_ruta: { icon: Truck, label: "En Ruta", color: "bg-blue-600", textColor: "text-blue-700", bgLight: "bg-blue-50" },
  entregado: { icon: MapPin, label: "Entregado", color: "bg-green-600", textColor: "text-green-700", bgLight: "bg-green-50" },
  cancelado: { icon: XCircle, label: "Cancelado", color: "bg-red-500", textColor: "text-red-600", bgLight: "bg-red-50" },
  // Estados adicionales de terceros
  pendiente: { icon: Clock, label: "Pendiente", color: "bg-amber-500", textColor: "text-amber-600", bgLight: "bg-amber-50" },
  pagado_pendiente_entrega: { icon: CreditCard, label: "Pagado - Pend. Entrega", color: "bg-blue-500", textColor: "text-blue-600", bgLight: "bg-blue-50" },
  en_transito: { icon: Truck, label: "En Tránsito", color: "bg-purple-500", textColor: "text-purple-600", bgLight: "bg-purple-50" },
};

const METODO_PAGO_CONFIG = {
  efectivo_vendedor: { icon: Banknote, label: "Efectivo Vendedor", color: "text-green-600" },
  transferencia_vendedor: { icon: Phone, label: "Transfer. Vendedor", color: "text-blue-600" },
  transferencia_baruj: { icon: Building2, label: "Transfer. Baruj", color: "text-purple-600" },
  contra_entrega: { icon: Truck, label: "Contra Entrega", color: "text-orange-600" },
  efectivo: { icon: Banknote, label: "Efectivo", color: "text-green-600" },
  transferencia: { icon: CreditCard, label: "Transferencia", color: "text-blue-600" },
};

// Formato CSV de ejemplo
const CSV_TEMPLATE = `cliente_nombre,cliente_telefono,direccion_entrega,barrio_entrega,fecha_entrega,franja_horaria,productos,metodo_pago,notas
"Juan Pérez","3001234567","Calle 100 #50-30","Usaquén","2026-06-01","manana","Torta Familiar x2, Torta Personal x1","contra_entrega","Entregar antes de las 10am"
"María López","3109876543","Carrera 7 #45-12","Chapinero","2026-06-01","tarde","Torta Mediana x3","transferencia_baruj","Llamar antes de entregar"`;

export default function AdminPedidosOnline() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendedores, setVendedores] = useState([]);
  const fileInputRef = useRef(null);
  
  // Filtros
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroVendedor, setFiltroVendedor] = useState("");
  const [filtroOrigen, setFiltroOrigen] = useState("");
  const [filtroFechaInicio, setFiltroFechaInicio] = useState("");
  const [filtroFechaFin, setFiltroFechaFin] = useState("");
  const [filtroBarrio, setFiltroBarrio] = useState("");
  const [filtroFranja, setFiltroFranja] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState("fecha_desc");
  
  // Selección múltiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Dialogs
  const [detalleDialog, setDetalleDialog] = useState({ open: false, pedido: null });
  const [bulkActionDialog, setBulkActionDialog] = useState({ open: false, action: "", nuevoEstado: "" });
  const [csvUploadDialog, setCsvUploadDialog] = useState({ open: false, data: [], errors: [], preview: [] });
  const [csvUploading, setCsvUploading] = useState(false);
  
  // Edición de método de pago
  const [editandoMetodoPago, setEditandoMetodoPago] = useState(false);
  const [nuevoMetodoPago, setNuevoMetodoPago] = useState("");
  const [motivoCambioPago, setMotivoCambioPago] = useState("");
  const [guardandoCambioPago, setGuardandoCambioPago] = useState(false);
  const [historialCambios, setHistorialCambios] = useState([]);
  
  // Edición de fecha de entrega
  const [editandoFecha, setEditandoFecha] = useState(false);
  const [nuevaFechaEntrega, setNuevaFechaEntrega] = useState("");
  const [nuevaFranjaHoraria, setNuevaFranjaHoraria] = useState("");
  const [motivoCambioFecha, setMotivoCambioFecha] = useState("");
  const [guardandoCambioFecha, setGuardandoCambioFecha] = useState(false);

  // Edición de artículos del pedido
  const [editandoItems, setEditandoItems] = useState(false);
  const [itemsEditados, setItemsEditados] = useState([]);
  const [motivoCambioItems, setMotivoCambioItems] = useState("");
  const [guardandoItems, setGuardandoItems] = useState(false);
  const [productosDisponibles, setProductosDisponibles] = useState([]);
  const [productoAgregar, setProductoAgregar] = useState("");

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch catalog orders
      let catalogUrl = "/catalogo/admin/orders?";
      if (filtroEstado) catalogUrl += `estado=${filtroEstado}&`;
      if (filtroVendedor) catalogUrl += `vendedor_id=${filtroVendedor}&`;
      if (filtroFechaInicio) catalogUrl += `fecha_inicio=${filtroFechaInicio}&`;
      if (filtroFechaFin) catalogUrl += `fecha_fin=${filtroFechaFin}&`;
      
      // Fetch third party orders
      let tercerosUrl = "/pedidos-terceros?";
      if (filtroEstado) tercerosUrl += `estado=${filtroEstado}&`;
      
      const [catalogRes, tercerosRes, vendedoresRes] = await Promise.all([
        api.get(catalogUrl),
        api.get(tercerosUrl),
        api.get("/usuarios?rol=vendedor")
      ]);
      
      // Transform and combine
      const catalogOrders = (catalogRes.data.pedidos || []).map(p => ({
        ...p,
        origen: "catalogo_digital",
        fecha_orden: p.fecha_creacion,
      }));
      
      const tercerosOrders = (tercerosRes.data || []).map(p => ({
        ...p,
        origen: "pedido_tercero",
        fecha_orden: p.fecha_creacion || p.created_at,
        numero_pedido: p.numero_pedido || `T-${p.id?.slice(0, 6)}`,
        cliente_nombre: p.cliente_nombre,
        cliente_telefono: p.cliente_telefono,
        direccion_entrega: p.cliente_direccion,
        barrio_entrega: p.barrio || "",
        items: p.items || [],
        total: p.total || 0,
        metodo_pago: p.metodo_pago || "efectivo",
        franja_horaria: p.franja_horaria || "",
      }));
      
      // Combine all orders
      const allOrders = [...catalogOrders, ...tercerosOrders];
      
      setPedidos(allOrders);
      setVendedores(vendedoresRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroVendedor, filtroFechaInicio, filtroFechaFin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar y ordenar
  const pedidosFiltrados = pedidos.filter(p => {
    // Búsqueda
    if (busqueda) {
      const search = busqueda.toLowerCase();
      const matchBusqueda = (
        p.numero_pedido?.toLowerCase().includes(search) ||
        p.cliente_nombre?.toLowerCase().includes(search) ||
        p.cliente_telefono?.includes(search) ||
        p.vendedor_nombre?.toLowerCase().includes(search) ||
        p.barrio_entrega?.toLowerCase().includes(search) ||
        p.direccion_entrega?.toLowerCase().includes(search)
      );
      if (!matchBusqueda) return false;
    }
    
    // Filtro por origen
    if (filtroOrigen && p.origen !== filtroOrigen) return false;
    
    // Filtro por barrio
    if (filtroBarrio && p.barrio_entrega?.toLowerCase() !== filtroBarrio.toLowerCase()) return false;
    
    // Filtro por franja
    if (filtroFranja && p.franja_horaria !== filtroFranja) return false;
    
    return true;
  }).sort((a, b) => {
    switch(ordenarPor) {
      case "fecha_asc":
        return new Date(a.fecha_orden) - new Date(b.fecha_orden);
      case "barrio":
        return (a.barrio_entrega || "").localeCompare(b.barrio_entrega || "");
      case "franja":
        const franjaOrder = { manana: 1, tarde: 2, noche: 3 };
        return (franjaOrder[a.franja_horaria] || 4) - (franjaOrder[b.franja_horaria] || 4);
      case "total_desc":
        return (b.total || 0) - (a.total || 0);
      case "fecha_desc":
      default:
        return new Date(b.fecha_orden) - new Date(a.fecha_orden);
    }
  });

  // Obtener lista única de barrios
  const barriosUnicos = [...new Set(pedidos.filter(p => p.barrio_entrega).map(p => p.barrio_entrega))].sort();

  // Contadores por estado
  const contadores = pedidos.reduce((acc, p) => {
    acc[p.estado] = (acc[p.estado] || 0) + 1;
    return acc;
  }, {});

  // Helpers
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Bogota"
    });
  };

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("es-CO", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "America/Bogota"
    });
  };

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pedidosFiltrados.map(p => p.id)));
    }
    setSelectAll(!selectAll);
  };

  // Bulk actions
  const handleBulkAction = async () => {
    if (selectedIds.size === 0) return;
    
    const { action, nuevoEstado } = bulkActionDialog;
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const id of Array.from(selectedIds)) {
        const pedido = pedidos.find(p => p.id === id);
        if (!pedido) continue;
        
        try {
          if (action === "cambiar_estado" && nuevoEstado) {
            if (pedido.origen === "catalogo_digital") {
              // Catalog orders use PATCH with query param
              await api.patch(`/catalogo/admin/orders/${id}/status?nuevo_estado=${nuevoEstado}`);
            } else {
              // Terceros orders use PUT with body
              await api.put(`/pedidos-terceros/${id}/estado`, { estado: nuevoEstado });
            }
            successCount++;
          }
        } catch (err) {
          console.error(`Error updating ${id}:`, err);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} pedido(s) actualizados`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} pedido(s) con error`);
      }
      
      setSelectedIds(new Set());
      setSelectAll(false);
      setBulkActionDialog({ open: false, action: "", nuevoEstado: "" });
      fetchData();
    } catch (error) {
      console.error("Bulk action error:", error);
      toast.error("Error al actualizar pedidos");
    }
  };

  // Cambiar método de pago con historial
  const handleCambiarMetodoPago = async () => {
    if (!detalleDialog.pedido || !nuevoMetodoPago || !motivoCambioPago.trim()) {
      toast.error("Selecciona un método de pago y describe el motivo del cambio");
      return;
    }
    
    const pedido = detalleDialog.pedido;
    const metodoPagoAnterior = pedido.metodo_pago;
    
    if (nuevoMetodoPago === metodoPagoAnterior) {
      toast.error("El método de pago es el mismo");
      return;
    }
    
    setGuardandoCambioPago(true);
    
    try {
      // Usar endpoint específico según el origen
      if (pedido.origen === "catalogo_digital") {
        await api.patch(`/catalogo/admin/orders/${pedido.id}/payment-method`, {
          metodo_pago: nuevoMetodoPago,
          motivo: motivoCambioPago,
          metodo_anterior: metodoPagoAnterior,
        });
      } else {
        await api.put(`/pedidos-terceros/${pedido.id}/metodo-pago`, {
          metodo_pago: nuevoMetodoPago,
          motivo: motivoCambioPago,
          metodo_anterior: metodoPagoAnterior,
        });
      }
      
      toast.success("Método de pago actualizado correctamente");
      
      // Reset estados de edición
      setEditandoMetodoPago(false);
      setNuevoMetodoPago("");
      setMotivoCambioPago("");
      
      // Refrescar datos
      fetchData();
      
      // Actualizar el diálogo con los datos nuevos
      const updatedPedido = {
        ...pedido,
        metodo_pago: nuevoMetodoPago,
      };
      setDetalleDialog({ open: true, pedido: updatedPedido });
      
      // Refrescar historial de cambios
      try {
        const histRes = await api.get(`/catalogo/admin/orders/${pedido.id}/historial`);
        setHistorialCambios(histRes.data.historial || []);
      } catch (err) {
        console.error("Error refreshing historial:", err);
      }
      
    } catch (error) {
      console.error("Error al cambiar método de pago:", error);
      toast.error(error.response?.data?.detail || "Error al cambiar método de pago");
    } finally {
      setGuardandoCambioPago(false);
    }
  };

  // Función para reprogramar fecha de entrega
  const handleReprogramarFecha = async () => {
    if (!detalleDialog.pedido || !nuevaFechaEntrega) {
      toast.error("Selecciona una nueva fecha de entrega");
      return;
    }
    
    const pedido = detalleDialog.pedido;
    const fechaAnterior = pedido.fecha_entrega;
    const franjaAnterior = pedido.franja_horaria;
    
    if (nuevaFechaEntrega === fechaAnterior && nuevaFranjaHoraria === franjaAnterior) {
      toast.error("La fecha y franja son las mismas");
      return;
    }
    
    setGuardandoCambioFecha(true);
    
    try {
      // Usar endpoint específico según el origen
      if (pedido.origen === "catalogo_digital") {
        await api.patch(`/catalogo/admin/orders/${pedido.id}/reschedule`, {
          fecha_entrega: nuevaFechaEntrega,
          franja_horaria: nuevaFranjaHoraria || pedido.franja_horaria,
          motivo: motivoCambioFecha || "Reprogramación solicitada",
          fecha_anterior: fechaAnterior,
          franja_anterior: franjaAnterior,
        });
      } else {
        await api.put(`/pedidos-terceros/${pedido.id}/reprogramar`, {
          fecha_entrega: nuevaFechaEntrega,
          franja_horaria: nuevaFranjaHoraria || pedido.franja_horaria,
          motivo: motivoCambioFecha || "Reprogramación solicitada",
        });
      }
      
      toast.success("Fecha de entrega reprogramada correctamente");
      
      // Reset estados de edición
      setEditandoFecha(false);
      setNuevaFechaEntrega("");
      setNuevaFranjaHoraria("");
      setMotivoCambioFecha("");
      
      // Refrescar datos
      fetchData();
      
      // Actualizar el diálogo con los datos nuevos
      const updatedPedido = {
        ...pedido,
        fecha_entrega: nuevaFechaEntrega,
        franja_horaria: nuevaFranjaHoraria || pedido.franja_horaria,
      };
      setDetalleDialog({ open: true, pedido: updatedPedido });
      
      // Refrescar historial de cambios
      try {
        const histRes = await api.get(`/catalogo/admin/orders/${pedido.id}/historial`);
        setHistorialCambios(histRes.data.historial || []);
      } catch (err) {
        console.error("Error refreshing historial:", err);
      }
      
    } catch (error) {
      console.error("Error al reprogramar fecha:", error);
      toast.error(error.response?.data?.detail || "Error al reprogramar fecha de entrega");
    } finally {
      setGuardandoCambioFecha(false);
    }
  };

  // ===== Edición de artículos del pedido (antes de confirmar) =====
  const ESTADOS_EDITABLES_ITEMS = ["recibido", "pendiente_confirmacion", "pago_pendiente", "pago_confirmado"];

  const iniciarEdicionItems = async () => {
    const pedido = detalleDialog.pedido;
    setItemsEditados((pedido.items || []).map((i) => ({ ...i })));
    setMotivoCambioItems("");
    setProductoAgregar("");
    setEditandoItems(true);
    if (productosDisponibles.length === 0) {
      try {
        const res = await api.get("/productos");
        setProductosDisponibles(res.data.filter((p) => p.activo));
      } catch (err) {
        console.error("Error cargando productos:", err);
      }
    }
  };

  const cambiarCantidadItem = (idx, delta) => {
    setItemsEditados((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, cantidad: Math.max(1, it.cantidad + delta) } : it))
    );
  };

  const eliminarItem = (idx) => {
    setItemsEditados((prev) => prev.filter((_, i) => i !== idx));
  };

  const agregarProducto = (productoId) => {
    const producto = productosDisponibles.find((p) => p.id === productoId);
    if (!producto) return;
    setItemsEditados((prev) => {
      const existente = prev.findIndex((i) => i.producto_id === producto.id);
      if (existente >= 0) {
        return prev.map((it, i) => (i === existente ? { ...it, cantidad: it.cantidad + 1 } : it));
      }
      return [
        ...prev,
        {
          producto_id: producto.id,
          producto_nombre: producto.nombre,
          cantidad: 1,
          precio_unitario: producto.precio,
          subtotal: producto.precio,
        },
      ];
    });
    setProductoAgregar("");
  };

  const handleGuardarItems = async () => {
    const pedido = detalleDialog.pedido;
    if (itemsEditados.length === 0) {
      toast.error("El pedido debe tener al menos un artículo");
      return;
    }
    setGuardandoItems(true);
    try {
      const res = await api.patch(`/catalogo/admin/orders/${pedido.id}/edit-items`, {
        items: itemsEditados.map((i) => ({
          producto_id: i.producto_id,
          producto_nombre: i.producto_nombre,
          cantidad: i.cantidad,
          precio_unitario: i.precio_unitario,
        })),
        motivo: motivoCambioItems || "Modificación de artículos por admin",
      });
      toast.success("Artículos del pedido actualizados");
      setEditandoItems(false);
      fetchData();
      setDetalleDialog({
        open: true,
        pedido: { ...pedido, items: res.data.items, subtotal: res.data.subtotal, total: res.data.total },
      });
      try {
        const histRes = await api.get(`/catalogo/admin/orders/${pedido.id}/historial`);
        setHistorialCambios(histRes.data.historial || []);
      } catch (err) {
        console.error("Error refreshing historial:", err);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al modificar los artículos");
    } finally {
      setGuardandoItems(false);
    }
  };

  // Abrir diálogo de detalle y cargar historial
  const openDetalleDialog = async (pedido) => {
    setDetalleDialog({ open: true, pedido });
    setEditandoMetodoPago(false);
    setNuevoMetodoPago("");
    setMotivoCambioPago("");
    setEditandoFecha(false);
    setNuevaFechaEntrega("");
    setNuevaFranjaHoraria("");
    setMotivoCambioFecha("");
    setEditandoItems(false);
    setItemsEditados([]);
    setMotivoCambioItems("");
    setProductoAgregar("");
    
    // Cargar historial de cambios si es del catálogo digital
    try {
      if (pedido.origen === "catalogo_digital") {
        const res = await api.get(`/catalogo/admin/orders/${pedido.id}/historial`);
        setHistorialCambios(res.data.historial || []);
      } else {
        setHistorialCambios([]);
      }
    } catch (err) {
      console.error("Error loading historial:", err);
      setHistorialCambios([]);
    }
  };

  // CSV Upload handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter(line => line.trim());
        const headers = lines[0].split(",").map(h => h.trim().replace(/"/g, ""));
        
        const data = [];
        const errors = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length !== headers.length) {
            errors.push(`Línea ${i + 1}: Número incorrecto de columnas`);
            continue;
          }
          
          const row = {};
          headers.forEach((h, idx) => {
            row[h] = values[idx];
          });
          
          // Validate required fields
          if (!row.cliente_nombre || !row.cliente_telefono) {
            errors.push(`Línea ${i + 1}: Faltan campos requeridos (cliente_nombre, cliente_telefono)`);
            continue;
          }
          
          data.push(row);
        }
        
        setCsvUploadDialog({
          open: true,
          data,
          errors,
          preview: data.slice(0, 5)
        });
      } catch (err) {
        toast.error("Error al procesar archivo CSV");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleCSVUpload = async () => {
    setCsvUploading(true);
    try {
      const { data } = csvUploadDialog;
      let successCount = 0;
      let errorCount = 0;
      
      for (const row of data) {
        try {
          // Parse products
          const productosText = row.productos || "";
          const items = productosText.split(",").map(p => {
            const match = p.trim().match(/(.+)\s*x\s*(\d+)/i);
            if (match) {
              return {
                producto_nombre: match[1].trim(),
                cantidad: parseInt(match[2]),
                precio_unitario: 0, // Will be calculated by backend
                subtotal: 0
              };
            }
            return null;
          }).filter(Boolean);
          
          // Create order via API
          await api.post("/catalogo/admin/orders/bulk", {
            cliente_nombre: row.cliente_nombre,
            cliente_telefono: row.cliente_telefono,
            direccion_entrega: row.direccion_entrega || "",
            barrio_entrega: row.barrio_entrega || "",
            fecha_entrega: row.fecha_entrega,
            franja_horaria: row.franja_horaria || "manana",
            items,
            metodo_pago: row.metodo_pago || "contra_entrega",
            notas: row.notas || "",
            origen: "carga_masiva"
          });
          successCount++;
        } catch (err) {
          errorCount++;
        }
      }
      
      toast.success(`${successCount} pedidos cargados, ${errorCount} errores`);
      setCsvUploadDialog({ open: false, data: [], errors: [], preview: [] });
      fetchData();
    } catch (error) {
      toast.error("Error al cargar pedidos");
    } finally {
      setCsvUploading(false);
    }
  };

  const downloadCSVTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "plantilla_pedidos.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    const headers = [
      "Número Pedido", "Origen", "Fecha", "Cliente", "Teléfono", "Vendedor",
      "Dirección", "Barrio", "Franja", "Productos", "Total", "Estado", "Método Pago"
    ];
    
    const rows = pedidosFiltrados.map(p => [
      p.numero_pedido,
      p.origen === "catalogo_digital" ? "Catálogo" : "Tercero",
      formatDate(p.fecha_orden),
      p.cliente_nombre,
      p.cliente_telefono,
      p.vendedor_nombre || "-",
      p.direccion_entrega || "-",
      p.barrio_entrega || "-",
      p.franja_horaria || "-",
      (p.items || []).map(i => `${i.producto_nombre} x${i.cantidad}`).join("; "),
      p.total,
      ESTADOS_CONFIG[p.estado]?.label || p.estado,
      METODO_PAGO_CONFIG[p.metodo_pago]?.label || p.metodo_pago
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fecha = new Date().toLocaleDateString("es-CO").replace(/\//g, "-");
    link.download = `pedidos_online_${fecha}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Stats
  const totalPedidos = pedidosFiltrados.length;
  const totalMonto = pedidosFiltrados.reduce((sum, p) => sum + (p.total || 0), 0);
  const pedidosCatalogo = pedidosFiltrados.filter(p => p.origen === "catalogo_digital").length;
  const pedidosTerceros = pedidosFiltrados.filter(p => p.origen === "pedido_tercero").length;

  return (
    <div className="space-y-6" data-testid="admin-pedidos-online">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-torta-dark">Administrador de Pedidos Online</h1>
          <p className="text-torta-muted text-sm">Gestiona todos los pedidos del catálogo y terceros en un solo lugar</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload className="w-4 h-4 mr-2" />
            Cargar CSV
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button variant="outline" size="sm" onClick={downloadCSVTemplate}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Plantilla
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-torta-brown to-[#A0522D] rounded-2xl p-4 text-white">
          <Package className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-white/80 text-xs">Total Pedidos</p>
          <p className="font-display text-2xl">{totalPedidos}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-2xl p-4 text-white">
          <CreditCard className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-white/80 text-xs">Total Monto</p>
          <p className="font-display text-2xl">{formatCurrency(totalMonto)}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <Building2 className="w-6 h-6 text-blue-500 mb-2" />
          <p className="text-blue-600 text-xs">Catálogo Digital</p>
          <p className="font-display text-2xl text-blue-700">{pedidosCatalogo}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
          <Truck className="w-6 h-6 text-purple-500 mb-2" />
          <p className="text-purple-600 text-xs">Pedidos Terceros</p>
          <p className="font-display text-2xl text-purple-700">{pedidosTerceros}</p>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">{selectedIds.size} pedido(s) seleccionado(s)</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkActionDialog({ open: true, action: "cambiar_estado", nuevoEstado: "" })}
            >
              <Settings className="w-4 h-4 mr-2" />
              Cambiar Estado
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedIds(new Set());
                setSelectAll(false);
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Deseleccionar
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-torta-sand/50 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-torta-muted" />
            <Input
              placeholder="Buscar por número, cliente, teléfono, barrio..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Quick filters */}
          <Select value={filtroOrigen || "all"} onValueChange={(v) => setFiltroOrigen(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Todos los orígenes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los orígenes</SelectItem>
              <SelectItem value="catalogo_digital">📱 Catálogo Digital</SelectItem>
              <SelectItem value="pedido_tercero">🚚 Pedido Tercero</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={ordenarPor} onValueChange={setOrdenarPor}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fecha_desc">📅 Más recientes</SelectItem>
              <SelectItem value="fecha_asc">📅 Más antiguos</SelectItem>
              <SelectItem value="barrio">📍 Por barrio</SelectItem>
              <SelectItem value="franja">🕐 Por franja</SelectItem>
              <SelectItem value="total_desc">💰 Mayor valor</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "border-torta-brown text-torta-brown" : ""}
          >
            <Filter className="w-4 h-4 mr-2" />
            Más Filtros
            {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </Button>
        </div>
        
        {/* Extended Filters */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4 pt-4 border-t border-torta-sand/50">
            <div>
              <Label className="text-xs text-torta-muted">Estado</Label>
              <Select value={filtroEstado || "all"} onValueChange={(v) => setFiltroEstado(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(ESTADOS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-torta-muted">Vendedor</Label>
              <Select value={filtroVendedor || "all"} onValueChange={(v) => setFiltroVendedor(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {vendedores.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-torta-muted">Barrio</Label>
              <Select value={filtroBarrio || "all"} onValueChange={(v) => setFiltroBarrio(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los barrios</SelectItem>
                  {barriosUnicos.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-torta-muted">Franja</Label>
              <Select value={filtroFranja || "all"} onValueChange={(v) => setFiltroFranja(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="manana">🌅 Mañana</SelectItem>
                  <SelectItem value="tarde">☀️ Tarde</SelectItem>
                  <SelectItem value="noche">🌙 Noche</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-torta-muted">Desde</Label>
              <Input 
                type="date" 
                value={filtroFechaInicio}
                onChange={(e) => setFiltroFechaInicio(e.target.value)}
              />
            </div>
            
            <div>
              <Label className="text-xs text-torta-muted">Hasta</Label>
              <Input 
                type="date" 
                value={filtroFechaFin}
                onChange={(e) => setFiltroFechaFin(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Estado pills */}
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={() => setFiltroEstado("")}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              !filtroEstado ? "bg-torta-brown text-white" : "bg-torta-cream text-torta-brown hover:bg-torta-sand"
            }`}
          >
            Todos ({pedidos.length})
          </button>
          {["recibido", "pendiente_confirmacion", "pago_pendiente", "en_produccion", "listo", "en_ruta", "entregado"].map((key) => {
            const cfg = ESTADOS_CONFIG[key];
            if (!cfg) return null;
            return (
              <button
                key={key}
                onClick={() => setFiltroEstado(key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                  filtroEstado === key ? `${cfg.color} text-white` : `${cfg.bgLight} ${cfg.textColor} hover:opacity-80`
                }`}
              >
                <cfg.icon className="w-3 h-3" />
                {cfg.label} ({contadores[key] || 0})
              </button>
            );
          })}
        </div>
      </div>

      {/* Matrix Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-torta-sand/50 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-torta-brown animate-spin mx-auto mb-2" />
            <p className="text-torta-muted">Cargando pedidos...</p>
          </div>
        ) : pedidosFiltrados.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-torta-sand mx-auto mb-3" />
            <p className="text-torta-muted">No hay pedidos que mostrar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-torta-cream/50 sticky top-0">
                <tr>
                  <th className="p-3 text-left">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-3 text-left font-medium text-torta-dark">Pedido</th>
                  <th className="p-3 text-left font-medium text-torta-dark">Origen</th>
                  <th className="p-3 text-left font-medium text-torta-dark">Cliente</th>
                  <th className="p-3 text-left font-medium text-torta-dark">Vendedor</th>
                  <th className="p-3 text-left font-medium text-torta-dark">Entrega</th>
                  <th className="p-3 text-left font-medium text-torta-dark">Barrio</th>
                  <th className="p-3 text-left font-medium text-torta-dark">Estado</th>
                  <th className="p-3 text-right font-medium text-torta-dark">Total</th>
                  <th className="p-3 text-center font-medium text-torta-dark">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-torta-sand/30">
                {pedidosFiltrados.map((pedido) => {
                  const estadoCfg = ESTADOS_CONFIG[pedido.estado] || ESTADOS_CONFIG.recibido;
                  const isSelected = selectedIds.has(pedido.id);
                  
                  return (
                    <tr 
                      key={pedido.id} 
                      className={`hover:bg-torta-cream/30 ${isSelected ? "bg-blue-50" : ""}`}
                    >
                      <td className="p-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(pedido.id)}
                        />
                      </td>
                      <td className="p-3">
                        <p className="font-display text-torta-brown font-semibold">{pedido.numero_pedido}</p>
                        <p className="text-xs text-torta-muted">{formatDate(pedido.fecha_orden)}</p>
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          pedido.origen === "catalogo_digital" 
                            ? "bg-blue-100 text-blue-700" 
                            : "bg-purple-100 text-purple-700"
                        }`}>
                          {pedido.origen === "catalogo_digital" ? "📱 Catálogo" : "🚚 Tercero"}
                        </span>
                      </td>
                      <td className="p-3">
                        <p className="font-medium text-torta-dark">{pedido.cliente_nombre}</p>
                        <p className="text-xs text-torta-muted flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {pedido.cliente_telefono}
                        </p>
                      </td>
                      <td className="p-3">
                        <p className="text-torta-dark">{pedido.vendedor_nombre || "-"}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-torta-dark">{formatDeliveryDate(pedido.fecha_entrega)}</p>
                        <p className="text-xs text-torta-muted">
                          {pedido.franja_horaria === "manana" && "🌅 Mañana"}
                          {pedido.franja_horaria === "tarde" && "☀️ Tarde"}
                          {pedido.franja_horaria === "noche" && "🌙 Noche"}
                        </p>
                      </td>
                      <td className="p-3">
                        <p className="text-torta-dark font-medium">{pedido.barrio_entrega || "-"}</p>
                        {pedido.direccion_entrega && (
                          <p className="text-xs text-torta-muted truncate max-w-[150px]" title={pedido.direccion_entrega}>
                            {pedido.direccion_entrega}
                          </p>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${estadoCfg.color}`}>
                          <estadoCfg.icon className="w-3 h-3" />
                          {estadoCfg.label}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <p className="font-display text-lg text-torta-brown">{formatCurrency(pedido.total)}</p>
                        <p className="text-xs text-torta-muted">{pedido.items?.length || 0} items</p>
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDetalleDialog(pedido)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detalleDialog.open} onOpenChange={(open) => setDetalleDialog({ open, pedido: null })}>
        <DialogContent className="max-w-lg bg-[#FBF5E8] border-2 border-[#E5D4B3]">
          {detalleDialog.pedido && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between border-b-2 border-dashed border-[#C8902A] pb-3">
                <div className="flex items-center gap-2">
                  <Package className="w-6 h-6 text-[#C8902A]" />
                  <span className="font-['Playfair_Display'] text-xl text-[#4A2C0A] font-bold">
                    {detalleDialog.pedido.numero_pedido}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    detalleDialog.pedido.origen === "catalogo_digital" 
                      ? "bg-blue-100 text-blue-700" 
                      : "bg-purple-100 text-purple-700"
                  }`}>
                    {detalleDialog.pedido.origen === "catalogo_digital" ? "Catálogo" : "Tercero"}
                  </span>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${
                  ESTADOS_CONFIG[detalleDialog.pedido.estado]?.color || "bg-gray-500"
                }`}>
                  {ESTADOS_CONFIG[detalleDialog.pedido.estado]?.label || detalleDialog.pedido.estado}
                </span>
              </div>

              {/* Cliente y Vendedor */}
              <div className="grid grid-cols-2 gap-4 bg-white rounded-lg p-3 border border-[#E5D4B3]">
                <div>
                  <p className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Cliente</p>
                  <p className="font-['Playfair_Display'] font-semibold text-[#4A2C0A]">{detalleDialog.pedido.cliente_nombre}</p>
                  <p className="text-sm text-[#4A2C0A]/70 font-['Poppins']">{detalleDialog.pedido.cliente_telefono}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Vendedor</p>
                  <p className="font-['Playfair_Display'] font-semibold text-[#4A2C0A]">{detalleDialog.pedido.vendedor_nombre || "-"}</p>
                </div>
              </div>
              
              {/* Productos */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Productos</p>
                  {!editandoItems &&
                    detalleDialog.pedido.origen === "catalogo_digital" &&
                    ESTADOS_EDITABLES_ITEMS.includes(detalleDialog.pedido.estado) && (
                      <button
                        onClick={iniciarEdicionItems}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        data-testid="edit-items-btn"
                      >
                        <Edit className="w-3 h-3" />
                        Editar artículos
                      </button>
                    )}
                </div>
                {!editandoItems ? (
                  <>
                    <div className="space-y-1">
                      {detalleDialog.pedido.items?.map((item, i) => (
                        <div key={i} className="flex justify-between items-center bg-red-50/50 p-3 rounded-lg border border-red-100">
                          <span className="font-['Poppins'] text-[#4A2C0A]">
                            <span className="font-semibold">{item.cantidad}x</span> {item.producto_nombre}
                          </span>
                          <span className="font-['Playfair_Display'] font-bold text-[#4A2C0A]">{formatCurrency(item.subtotal || item.precio_unitario * item.cantidad)}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t-2 border-dashed border-[#C8902A]">
                      <div className="flex justify-between font-['Playfair_Display'] text-xl font-bold">
                        <span className="text-[#4A2C0A]">Total</span>
                        <span className="text-[#C8902A]">{formatCurrency(detalleDialog.pedido.total)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2 bg-white rounded-lg p-3 border-2 border-blue-200" data-testid="edit-items-panel">
                    {itemsEditados.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-blue-50/50 p-2 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-['Poppins'] text-[#4A2C0A] truncate">{item.producto_nombre}</p>
                          <p className="text-xs text-gray-500">{formatCurrency(item.precio_unitario)} c/u</p>
                        </div>
                        <button
                          onClick={() => cambiarCantidadItem(idx, -1)}
                          className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-gray-100"
                          data-testid={`item-minus-${item.producto_nombre}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center font-semibold text-sm" data-testid={`item-qty-${item.producto_nombre}`}>{item.cantidad}</span>
                        <button
                          onClick={() => cambiarCantidadItem(idx, 1)}
                          className="w-7 h-7 rounded-full bg-[#4A2C0A] text-white flex items-center justify-center hover:bg-[#3a2208]"
                          data-testid={`item-plus-${item.producto_nombre}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => eliminarItem(idx)}
                          className="w-7 h-7 rounded-full text-red-500 flex items-center justify-center hover:bg-red-50"
                          data-testid={`item-remove-${item.producto_nombre}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <Select value={productoAgregar} onValueChange={agregarProducto}>
                      <SelectTrigger className="h-9 text-xs" data-testid="add-product-select">
                        <SelectValue placeholder="+ Agregar producto..." />
                      </SelectTrigger>
                      <SelectContent>
                        {productosDisponibles.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nombre} — {formatCurrency(p.precio)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="pt-2 border-t border-dashed border-[#C8902A] space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span>{formatCurrency(itemsEditados.reduce((s, i) => s + i.cantidad * i.precio_unitario, 0))}</span>
                      </div>
                      {(detalleDialog.pedido.costo_domicilio || 0) > 0 && (
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Domicilio</span>
                          <span>{formatCurrency(detalleDialog.pedido.costo_domicilio)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-[#4A2C0A]">
                        <span>Nuevo Total</span>
                        <span className="text-[#C8902A]" data-testid="nuevo-total">
                          {formatCurrency(
                            itemsEditados.reduce((s, i) => s + i.cantidad * i.precio_unitario, 0) +
                              (detalleDialog.pedido.costo_domicilio || 0)
                          )}
                        </span>
                      </div>
                    </div>

                    <Textarea
                      value={motivoCambioItems}
                      onChange={(e) => setMotivoCambioItems(e.target.value)}
                      placeholder="Motivo del cambio (ej: Cliente pidió agregar una torta)"
                      className="text-xs h-14 resize-none"
                      data-testid="edit-items-reason"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditandoItems(false)}
                        className="flex-1 h-8 text-xs"
                        data-testid="cancel-edit-items-btn"
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleGuardarItems}
                        disabled={guardandoItems || itemsEditados.length === 0}
                        className="flex-1 h-8 text-xs bg-blue-600 hover:bg-blue-700"
                        data-testid="save-edit-items-btn"
                      >
                        {guardandoItems ? "Guardando..." : "Guardar cambios"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Entrega y Pago */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-3 border border-[#E5D4B3]">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Entrega</p>
                    {!editandoFecha && !["entregado", "cancelado"].includes(detalleDialog.pedido.estado) && (
                      <button
                        onClick={() => {
                          setEditandoFecha(true);
                          setNuevaFechaEntrega(detalleDialog.pedido.fecha_entrega?.split("T")[0] || "");
                          setNuevaFranjaHoraria(detalleDialog.pedido.franja_horaria || "");
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        data-testid="edit-delivery-date-btn"
                      >
                        <Edit className="w-3 h-3" />
                        Reprogramar
                      </button>
                    )}
                  </div>
                  {!editandoFecha ? (
                    <>
                      <p className="font-['Playfair_Display'] font-semibold text-[#4A2C0A]">
                        {formatDeliveryDate(detalleDialog.pedido.fecha_entrega)}
                      </p>
                      <p className="text-xs text-[#4A2C0A]/70 font-['Poppins']">
                        {detalleDialog.pedido.franja_horaria === "manana" ? "🌅 Mañana" : 
                         detalleDialog.pedido.franja_horaria === "tarde" ? "☀️ Tarde" : 
                         detalleDialog.pedido.franja_horaria === "noche" ? "🌙 Noche" : "-"}
                      </p>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        type="date"
                        value={nuevaFechaEntrega}
                        onChange={(e) => setNuevaFechaEntrega(e.target.value)}
                        className="h-8 text-xs"
                        data-testid="new-delivery-date-input"
                      />
                      <Select
                        value={nuevaFranjaHoraria}
                        onValueChange={setNuevaFranjaHoraria}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Franja horaria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manana">🌅 Mañana</SelectItem>
                          <SelectItem value="tarde">☀️ Tarde</SelectItem>
                          <SelectItem value="noche">🌙 Noche</SelectItem>
                        </SelectContent>
                      </Select>
                      <Textarea
                        value={motivoCambioFecha}
                        onChange={(e) => setMotivoCambioFecha(e.target.value)}
                        placeholder="Motivo de reprogramación (ej: Cliente no disponible)"
                        className="text-xs h-14 resize-none"
                        data-testid="reschedule-reason"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditandoFecha(false);
                            setNuevaFechaEntrega("");
                            setNuevaFranjaHoraria("");
                            setMotivoCambioFecha("");
                          }}
                          className="flex-1 h-7 text-xs"
                        >
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleReprogramarFecha}
                          disabled={guardandoCambioFecha || !nuevaFechaEntrega}
                          className="flex-1 h-7 text-xs bg-blue-600 hover:bg-blue-700"
                          data-testid="save-reschedule-btn"
                        >
                          {guardandoCambioFecha ? "Guardando..." : "Guardar"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-lg p-3 border border-[#E5D4B3]">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Método de Pago</p>
                    {!editandoMetodoPago && !["entregado", "cancelado"].includes(detalleDialog.pedido.estado) && (
                      <button
                        onClick={() => {
                          setEditandoMetodoPago(true);
                          setNuevoMetodoPago(detalleDialog.pedido.metodo_pago);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        data-testid="edit-payment-method-btn"
                      >
                        <Edit className="w-3 h-3" />
                        Cambiar
                      </button>
                    )}
                  </div>
                  {!editandoMetodoPago ? (
                    <p className="font-['Playfair_Display'] font-semibold text-[#4A2C0A]">
                      {METODO_PAGO_CONFIG[detalleDialog.pedido.metodo_pago]?.label || detalleDialog.pedido.metodo_pago}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <Select
                        value={nuevoMetodoPago}
                        onValueChange={setNuevoMetodoPago}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(METODO_PAGO_CONFIG)
                            .filter(([key]) => {
                              // Para pedidos del catálogo digital, solo mostrar métodos válidos del backend
                              if (detalleDialog.pedido?.origen === "catalogo_digital") {
                                return ["pago_vendedor", "efectivo_vendedor", "transferencia_vendedor", "transferencia_baruj", "contra_entrega"].includes(key);
                              }
                              // Para terceros, mostrar todos
                              return true;
                            })
                            .map(([key, val]) => (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center gap-2">
                                <val.icon className="w-3 h-3" />
                                {val.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Textarea
                        value={motivoCambioPago}
                        onChange={(e) => setMotivoCambioPago(e.target.value)}
                        placeholder="Motivo del cambio (ej: Cliente pagó por anticipado)"
                        className="text-xs h-16 resize-none"
                        data-testid="payment-change-reason"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditandoMetodoPago(false);
                            setNuevoMetodoPago("");
                            setMotivoCambioPago("");
                          }}
                          className="flex-1 h-7 text-xs"
                        >
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCambiarMetodoPago}
                          disabled={guardandoCambioPago || !nuevoMetodoPago || !motivoCambioPago.trim()}
                          className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
                          data-testid="save-payment-method-btn"
                        >
                          {guardandoCambioPago ? "Guardando..." : "Guardar"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Dirección con Ciudad */}
              {detalleDialog.pedido.direccion_entrega && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-xs text-blue-600 font-['Poppins'] mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Dirección de Entrega
                  </p>
                  <p className="font-['Poppins'] font-semibold text-[#4A2C0A]">{detalleDialog.pedido.direccion_entrega}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    {detalleDialog.pedido.ciudad && (
                      <p className="text-sm text-[#4A2C0A]/70 font-['Poppins']">
                        <Building2 className="w-3 h-3 inline mr-1" />
                        {detalleDialog.pedido.ciudad}
                        {detalleDialog.pedido.departamento && `, ${detalleDialog.pedido.departamento}`}
                      </p>
                    )}
                    {detalleDialog.pedido.barrio_entrega && (
                      <p className="text-sm text-[#4A2C0A]/70 font-['Poppins']">
                        Barrio: {detalleDialog.pedido.barrio_entrega}
                      </p>
                    )}
                  </div>
                  {detalleDialog.pedido.zona_envio_nombre && (
                    <p className="text-xs text-blue-600 mt-1 font-['Poppins']">
                      Zona: {detalleDialog.pedido.zona_envio_nombre} • Domicilio: {formatCurrency(detalleDialog.pedido.costo_domicilio)}
                    </p>
                  )}
                </div>
              )}

              {/* Notas */}
              {detalleDialog.pedido.notas && (
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-xs text-amber-600 font-['Poppins'] mb-1">📝 Notas</p>
                  <p className="font-['Poppins'] text-[#4A2C0A]">{detalleDialog.pedido.notas}</p>
                </div>
              )}

              {/* Historial de Cambios */}
              {detalleDialog.pedido.requiere_factura && (
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200" data-testid="factura-info-section">
                  <p className="text-xs font-semibold text-purple-800 font-['Poppins'] mb-2 flex items-center gap-1">
                    <FileSpreadsheet className="w-3.5 h-3.5" /> Requiere Factura Electrónica (DIAN)
                  </p>
                  {detalleDialog.pedido.datos_facturacion && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-purple-900 font-['Poppins']">
                      <p><span className="text-purple-500">Tipo:</span> {detalleDialog.pedido.datos_facturacion.tipo_persona === "juridica" ? "Persona Jurídica" : "Persona Natural"}</p>
                      <p><span className="text-purple-500">Documento:</span> {detalleDialog.pedido.datos_facturacion.tipo_documento} {detalleDialog.pedido.datos_facturacion.numero_documento}{detalleDialog.pedido.datos_facturacion.dv ? `-${detalleDialog.pedido.datos_facturacion.dv}` : ""}</p>
                      <p className="col-span-2"><span className="text-purple-500">Razón social:</span> {detalleDialog.pedido.datos_facturacion.razon_social}</p>
                      <p className="col-span-2"><span className="text-purple-500">Email:</span> {detalleDialog.pedido.datos_facturacion.email_facturacion}</p>
                      <p><span className="text-purple-500">Dirección:</span> {detalleDialog.pedido.datos_facturacion.direccion_fiscal}</p>
                      <p><span className="text-purple-500">Ciudad:</span> {detalleDialog.pedido.datos_facturacion.ciudad_fiscal}</p>
                      <p className="col-span-2"><span className="text-purple-500">IVA:</span> {detalleDialog.pedido.datos_facturacion.responsable_iva === "responsable" ? "Responsable de IVA" : "No responsable de IVA"}</p>
                    </div>
                  )}
                </div>
              )}

              {historialCambios.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs text-gray-600 font-['Poppins'] mb-2 flex items-center gap-1">
                    <History className="w-3 h-3" /> Historial de Cambios
                  </p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {historialCambios.map((cambio, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-gray-100">
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-[#4A2C0A]">{cambio.tipo}</span>
                          <span className="text-gray-400">{formatDate(cambio.fecha)}</span>
                        </div>
                        {cambio.valor_anterior && cambio.valor_nuevo && (
                          <p className="text-gray-600 mt-1">
                            {cambio.valor_anterior} → {cambio.valor_nuevo}
                          </p>
                        )}
                        {cambio.motivo && (
                          <p className="text-gray-500 italic mt-1">&quot;{cambio.motivo}&quot;</p>
                        )}
                        <p className="text-gray-400 text-[10px] mt-1">Por: {cambio.usuario_nombre || "Sistema"}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialog.open} onOpenChange={(open) => setBulkActionDialog({ ...bulkActionDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Acción Masiva - {selectedIds.size} pedido(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nuevo Estado</Label>
              <Select 
                value={bulkActionDialog.nuevoEstado} 
                onValueChange={(v) => setBulkActionDialog({ ...bulkActionDialog, nuevoEstado: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ESTADOS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <val.icon className="w-4 h-4" />
                        {val.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Esta acción actualizará {selectedIds.size} pedido(s) al estado seleccionado.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialog({ open: false, action: "", nuevoEstado: "" })}>
              Cancelar
            </Button>
            <Button onClick={handleBulkAction} disabled={!bulkActionDialog.nuevoEstado}>
              Aplicar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={csvUploadDialog.open} onOpenChange={(open) => setCsvUploadDialog({ ...csvUploadDialog, open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Cargar Pedidos desde CSV
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {csvUploadDialog.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 font-medium text-sm mb-2">Errores encontrados:</p>
                <ul className="text-red-600 text-xs space-y-1">
                  {csvUploadDialog.errors.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div>
              <p className="text-sm font-medium mb-2">Vista previa ({csvUploadDialog.data.length} registros):</p>
              <div className="border rounded-lg overflow-x-auto max-h-[300px]">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Cliente</th>
                      <th className="p-2 text-left">Teléfono</th>
                      <th className="p-2 text-left">Dirección</th>
                      <th className="p-2 text-left">Barrio</th>
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 text-left">Productos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {csvUploadDialog.preview.map((row, i) => (
                      <tr key={i}>
                        <td className="p-2">{row.cliente_nombre}</td>
                        <td className="p-2">{row.cliente_telefono}</td>
                        <td className="p-2 truncate max-w-[150px]">{row.direccion_entrega}</td>
                        <td className="p-2">{row.barrio_entrega}</td>
                        <td className="p-2">{row.fecha_entrega}</td>
                        <td className="p-2 truncate max-w-[200px]">{row.productos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {csvUploadDialog.data.length > 5 && (
                <p className="text-xs text-gray-500 mt-1">... y {csvUploadDialog.data.length - 5} más</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCsvUploadDialog({ open: false, data: [], errors: [], preview: [] })}>
              Cancelar
            </Button>
            <Button onClick={handleCSVUpload} disabled={csvUploading || csvUploadDialog.data.length === 0}>
              {csvUploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Cargando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Cargar {csvUploadDialog.data.length} pedidos
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
